import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Page header for inner pages.
 *
 * Visual language matches the newer PageHost-based routes (/, /network,
 * /network/erc-3643, /network/ledger): large fluid display serif, generous
 * vertical rhythm, subtle radial bloom on night, champagne CTA with inner
 * highlights.
 *
 * @param {object} props
 * @param {string} props.eyebrow uppercase eyebrow text
 * @param {string} props.headline main headline (no italic accent)
 * @param {string} [props.highlightWord] word in headline to wrap in italic accent
 * @param {string} props.subheadline
 * @param {{label:string,href:string}} [props.primaryCta]
 * @param {{label:string,href:string}} [props.secondaryCta]
 * @param {boolean} [props.compact]
 * @param {boolean} [props.matchLandingHero] use the landing hero's exact
 *   typography (h1 clamp / line-height, subheadline clamp / width). Keeps
 *   other consumers of PageHeader on their current visual weights.
 * @param {React.ReactNode} [props.sideElement]
 * @param {"night"|"day"} [props.tone="night"]
 */
export function PageHeader({
  eyebrow,
  headline,
  highlightWord,
  subheadline,
  primaryCta,
  secondaryCta,
  compact = false,
  matchLandingHero = false,
  sideElement,
  tone = "night",
}) {
  const isDay = tone === "day";

  const renderHeadline = () => {
    if (!highlightWord) return headline;
    const idx = headline.toLowerCase().indexOf(highlightWord.toLowerCase());
    if (idx === -1) return headline;
    return (
      <>
        {headline.slice(0, idx)}
        <em className="accent-italic font-serif italic">{headline.slice(idx, idx + highlightWord.length)}</em>
        {headline.slice(idx + highlightWord.length)}
      </>
    );
  };

  const surface = isDay ? "surface-day" : "surface-night with-bloom";
  const titleColor = isDay ? "text-aubergine" : "text-bone";
  const bodyColor = isDay ? "text-aubergine/70" : "text-bone/70";

  // Primary CTA: same paint stack as the homepage "Read our technical
  // documentation" button (.btn-primary in landing-styles.js, driven by
  // `--gradient-champ-night`). Fully compiled so it works even inside
  // React trees that don't import landing-styles.
  const primaryStyle = {
    backgroundImage:
      "radial-gradient(ellipse at 30% 30%, #FBEFD0 0%, #E8D9B8 50%, #A88B4A 100%)",
    color: "var(--aubergine)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(92,72,36,0.55), 0 0 24px rgba(232,217,184,0.20)",
  };
  const primaryCls =
    "inline-flex items-center justify-center rounded-md px-6 py-3.5 font-mono text-[13px] font-medium tracking-[0.04em] hover:brightness-[1.06] transition-all";
  const secondaryCls = isDay
    ? "inline-flex items-center justify-center rounded-md border border-aubergine/30 text-aubergine hover:bg-aubergine/5 px-6 py-3.5 font-mono text-[13px] font-medium tracking-[0.04em] transition-all"
    : "inline-flex items-center justify-center rounded-md border border-champagne/50 text-bone hover:bg-champagne/10 px-6 py-3.5 font-mono text-[13px] font-medium tracking-[0.04em] transition-all";

  const spacing = compact
    ? "pt-28 pb-14 lg:pt-32 lg:pb-20"
    : "pt-32 pb-20 lg:pt-40 lg:pb-28";

  // Fluid hero type , mirrors landing hero (.trex-hero-host h1.headline)
  // exactly so every inner page H1 renders at the same fluid size, weight
  // and font stack as the landing page, regardless of the `compact` flag.
  // (Compact only changes the outer section padding, never the H1 scale.)
  // `matchLandingHero` snaps the H1 clamp and line-height to the landing
  // file's exact values (clamp(38px,5.6vw,76px), line-height 1.12) so
  // opt-in pages (currently Terms of use) render identically to the
  // landing hero rather than the slightly heavier PageHeader default.
  const headlineStyle = matchLandingHero
    ? {
        fontFamily: "var(--font-display)",
        fontWeight: 400,
        fontSize: "clamp(38px, 5.6vw, 76px)",
        lineHeight: 1.12,
        letterSpacing: "-0.02em",
      }
    : {
        fontFamily: "var(--font-display)",
        fontWeight: 400,
        fontSize: "clamp(2rem, 5.6vw, 5.5rem)",
        lineHeight: 1.05,
        letterSpacing: "-0.025em",
      };

  // Subheadline: landing hero `.subline` uses
  // `font-size:clamp(17px,1.7vw,21px); line-height:1.5; max-width:46ch;
  //  color: rgba(250,248,244,0.80)`. When `matchLandingHero` is set we
  // reproduce those numbers exactly (font-family stays on the site body
  // stack via Tailwind's leading-relaxed already applied to the <p>).
  const subheadlineStyle = matchLandingHero
    ? { fontSize: "clamp(17px, 1.7vw, 21px)", lineHeight: 1.5, maxWidth: "46ch" }
    : undefined;

  return (
    <section className={`relative ${surface} ${spacing}`} data-testid="page-header">
      {/* Subtle radial bloom in the top-left, mirrored on the right on wide screens.
          Only rendered on night tone to preserve the day surface's flatness. */}
      {!isDay && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            background:
              "radial-gradient(ellipse 60rem 42rem at 12% 30%, rgba(232,217,184,0.11) 0%, rgba(232,217,184,0) 60%), radial-gradient(ellipse 42rem 32rem at 88% 68%, rgba(201,163,74,0.08) 0%, rgba(201,163,74,0) 60%)",
          }}
        />
      )}

      <div className={`relative z-10 mx-auto ${matchLandingHero ? "max-w-3xl" : "max-w-7xl"} px-6 lg:px-8`}>
        <div className={sideElement ? "grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-12 items-start" : ""}>
          <div className={matchLandingHero ? "max-w-none" : "max-w-4xl"}>
            {eyebrow && (
              <p className="font-mono text-[11px] font-medium text-champagne uppercase tracking-[0.24em] mb-6">
                {eyebrow}
              </p>
            )}
            <h1
              className={`font-serif font-normal tracking-tight text-balance ${titleColor}`}
              style={headlineStyle}
              data-testid="page-header-h1"
            >
              {renderHeadline()}
            </h1>
            <p
              className={`mt-7 leading-relaxed text-pretty ${bodyColor} ${matchLandingHero ? "" : (compact ? "text-base max-w-xl" : "text-lg lg:text-xl max-w-2xl")}`}
              style={subheadlineStyle}
              data-testid="page-header-subheadline"
            >
              {subheadline}
            </p>

            {(primaryCta || secondaryCta) && (
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                {primaryCta && (
                  <Link
                    to={primaryCta.href}
                    data-testid="page-header-primary-cta"
                    className={primaryCls}
                    style={primaryStyle}
                  >
                    {primaryCta.label}
                    <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Link>
                )}
                {secondaryCta && (
                  <a
                    href={secondaryCta.href}
                    data-testid="page-header-secondary-cta"
                    className={secondaryCls}
                  >
                    {secondaryCta.label}
                  </a>
                )}
              </div>
            )}
          </div>

          {sideElement && <div className="hidden lg:block">{sideElement}</div>}
        </div>
      </div>
    </section>
  );
}
